import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { useReactToPrint } from 'react-to-print';
import { getRouteForPrint, RouteLocation, IPrintRoute } from '../services/route-for-print';

interface IUseGuestLicensePrintParams {
  isMapsLoaded: boolean;
  origin?: RouteLocation;
  destination?: RouteLocation;
  guestId?: number;
  fastAccessEnabled: boolean;
  obtainFastAccessPass: (guestId: number) => Promise<string>;
}

export function useGuestPrintInstruction({
  isMapsLoaded,
  origin,
  destination,
  guestId,
  fastAccessEnabled,
  obtainFastAccessPass,
}: IUseGuestLicensePrintParams) {
  const printRef = useRef<HTMLDivElement>(null);

  const [route, setRoute] = useState<IPrintRoute | null>(null);
  const [fastAccessPassId, setFastAccessPassId] = useState<string | null>(null);

  const loadRoute = async (): Promise<IPrintRoute | null> => {
    if (!isMapsLoaded || !origin || !destination) {
      return null;
    }

    try {
      return await getRouteForPrint({
        origin,
        destination,
      });
    } catch (error) {
      console.error('Failed to compute print route:', error);
      return null;
    }
  };

  const loadFastAccessPass = async (): Promise<string | null> => {
    if (!fastAccessEnabled || !guestId) {
      return null;
    }

    try {
      return await obtainFastAccessPass(guestId);
    } catch (error) {
      console.error('Failed to create fast access pass:', error);
      return null;
    }
  };

  const handlePrint = useReactToPrint({
    content: () => printRef.current,

    onBeforeGetContent: async () => {
      const [nextRoute, nextFastAccessPassId] = await Promise.all([loadRoute(), loadFastAccessPass()]);
      flushSync(() => {
        setRoute(nextRoute);
        setFastAccessPassId(nextFastAccessPassId);
      });
    },
  });

  return {
    printRef,
    route,
    fastAccessPassId,
    handlePrint,
  };
}
